import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot, orderBy, query, serverTimestamp, setDoc } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { db } from '../../firebaseConfig';
import { useAuth } from '../../auth/AuthProvider';
import { formatMoney, normalizePhone } from '../lib/intakeCatalog';

const inputStyle = {
  width: '100%',
  background: '#0b1727',
  color: '#e7f4ff',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 14,
  padding: '12px 14px',
  outline: 'none',
  boxSizing: 'border-box',
};

function emptyEditor() {
  return {
    name: '',
    phone: '',
    suburb: '',
    addressLine1: '',
    notes: '',
  };
}

function formatLastOrder(value) {
  if (!value) return '—';
  try {
    const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString();
  } catch {
    return '—';
  }
}

export default function OnTimeCustomersPage() {
  const { claims } = useAuth();
  const orgId = claims?.orgId || '';
  const role = String(claims?.role || '').toLowerCase();
  const canEdit = Boolean(claims?.orgAdmin) || role.includes('owner') || role.includes('admin') || Boolean(claims?.superAdmin) || Boolean(claims?.superadmin);

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [editor, setEditor] = useState(emptyEditor());
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!orgId) {
      setCustomers([]);
      setLoading(false);
      return undefined;
    }

    const ref = collection(db, 'orgs', orgId, 'ontimeCustomers');
    const unsub = onSnapshot(
      query(ref, orderBy('updatedAt', 'desc')),
      (snap) => {
        const rows = snap.docs.map((row) => ({ id: row.id, ...row.data() }));
        setCustomers(rows);
        setLoading(false);
        setSelectedId((prev) => prev || rows[0]?.id || '');
      },
      (err) => {
        console.error('[OnTime Customers] load failed', err);
        setError(err?.message || 'Failed to load customers. Check Firestore permissions.');
        setCustomers([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [orgId]);

  const filteredCustomers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((customer) => {
      const hay = [
        customer.name,
        customer.phone,
        customer.suburb,
        customer.addressLine1,
        customer.stats?.lastOrderNumber,
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [customers, search]);

  const selectedCustomer = filteredCustomers.find((customer) => customer.id === selectedId)
    || customers.find((customer) => customer.id === selectedId)
    || null;

  useEffect(() => {
    if (!selectedCustomer) {
      setEditor(emptyEditor());
      return;
    }
    setEditor({
      name: selectedCustomer.name || '',
      phone: selectedCustomer.phone || '',
      suburb: selectedCustomer.suburb || '',
      addressLine1: selectedCustomer.addressLine1 || '',
      notes: selectedCustomer.notes || '',
    });
  }, [selectedCustomer?.id]);

  async function handleSave(e) {
    e.preventDefault();
    setMessage('');
    setError('');

    if (!selectedCustomer || !orgId) return;
    if (!canEdit) {
      setError('This login can view customers but cannot edit them.');
      return;
    }
    if (!editor.name.trim()) {
      setError('Customer name is required.');
      return;
    }

    setSaving(true);
    try {
      await setDoc(
        doc(db, 'orgs', orgId, 'ontimeCustomers', selectedCustomer.id),
        {
          name: editor.name.trim(),
          phone: editor.phone.trim(),
          normalizedPhone: normalizePhone(editor.phone),
          suburb: editor.suburb.trim(),
          addressLine1: editor.addressLine1.trim(),
          notes: editor.notes.trim(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setMessage('Customer updated.');
    } catch (err) {
      console.error('[OnTime Customers] save failed', err);
      setError(err?.message || 'Failed to update customer.');
    } finally {
      setSaving(false);
    }
  }

  const activeCount = customers.filter((customer) => Number(customer.stats?.orderCount || 0) > 0).length;
  const totalSpend = customers.reduce((sum, customer) => sum + Number(customer.stats?.totalSpend || 0), 0);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-semibold text-white">Customers</h2>
        <p className="mt-2 text-sm text-slate-400">
          CRM light for pilot use. Search repeat customers, confirm delivery details and keep intake fast.
        </p>
      </div>

      {message ? <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">{message}</div> : null}
      {error ? <div className="rounded-2xl border border-rose-400/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">{error}</div> : null}

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Saved customers</div>
          <div className="mt-3 text-3xl font-semibold text-white">{customers.length}</div>
          <div className="mt-2 text-sm text-slate-400">Built automatically from completed intake orders.</div>
        </div>
        <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Repeat order base</div>
          <div className="mt-3 text-3xl font-semibold text-white">{activeCount}</div>
          <div className="mt-2 text-sm text-slate-400">Customers with at least one captured order.</div>
        </div>
        <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Tracked spend</div>
          <div className="mt-3 text-3xl font-semibold text-white">{formatMoney(totalSpend)}</div>
          <div className="mt-2 text-sm text-slate-400">Running total from Link pilot orders.</div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[420px_minmax(0,1fr)]">
        <section className="rounded-3xl border border-white/10 bg-white/5 p-5">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-slate-500">Customer directory</div>
              <div className="mt-1 text-sm text-slate-400">Search by name, phone, suburb or last order number.</div>
            </div>
            <Link
              to="/ontime/intake"
              className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-3 py-1.5 text-sm text-cyan-200"
            >
              Open Intake
            </Link>
          </div>

          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search saved customer"
            style={inputStyle}
          />

          <div className="mt-4 space-y-2">
            {loading ? <div className="rounded-2xl border border-white/10 bg-[#0b1727] px-4 py-4 text-sm text-slate-400">Loading customers…</div> : null}
            {!loading && !filteredCustomers.length ? (
              <div className="rounded-2xl border border-white/10 bg-[#0b1727] px-4 py-4 text-sm text-slate-400">
                No customers found yet. Once the online team creates orders in Intake, customers will start appearing here automatically.
              </div>
            ) : null}
            {filteredCustomers.map((customer) => {
              const isActive = customer.id === selectedId;
              return (
                <button
                  key={customer.id}
                  type="button"
                  onClick={() => setSelectedId(customer.id)}
                  className={`w-full rounded-2xl border px-4 py-3 text-left transition ${
                    isActive
                      ? 'border-cyan-400/40 bg-cyan-500/10'
                      : 'border-white/10 bg-[#0b1727] hover:border-cyan-400/20'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-medium text-white">{customer.name || 'Unnamed customer'}</div>
                      <div className="mt-1 text-sm text-slate-400">{customer.phone || 'No phone'} • {customer.suburb || 'No suburb'}</div>
                    </div>
                    <div className="text-right text-xs text-slate-500">
                      <div>{Number(customer.stats?.orderCount || 0)} orders</div>
                      <div className="mt-1 text-cyan-200">{formatMoney(customer.stats?.totalSpend || 0)}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <section className="rounded-3xl border border-white/10 bg-white/5 p-5">
          {!selectedCustomer ? (
            <div className="rounded-2xl border border-white/10 bg-[#0b1727] px-4 py-4 text-sm text-slate-400">
              Select a customer on the left to view and edit details.
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-slate-500">Customer profile</div>
                  <h3 className="mt-1 text-2xl font-semibold text-white">{selectedCustomer.name || 'Customer'}</h3>
                  <div className="mt-2 text-sm text-slate-400">Last order: {selectedCustomer.stats?.lastOrderNumber || '—'} • {formatLastOrder(selectedCustomer.stats?.lastOrderAt)}</div>
                </div>
                <Link
                  to="/ontime/intake"
                  className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-slate-200"
                >
                  Use in Intake
                </Link>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-2xl border border-white/10 bg-[#0b1727] p-4">
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Order count</div>
                  <div className="mt-2 text-2xl font-semibold text-white">{Number(selectedCustomer.stats?.orderCount || 0)}</div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-[#0b1727] p-4">
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Total spend</div>
                  <div className="mt-2 text-2xl font-semibold text-white">{formatMoney(selectedCustomer.stats?.totalSpend || 0)}</div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-[#0b1727] p-4">
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Last order</div>
                  <div className="mt-2 text-lg font-semibold text-cyan-200">{selectedCustomer.stats?.lastOrderNumber || '—'}</div>
                </div>
              </div>

              <form onSubmit={handleSave} className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_260px]">
                <div className="space-y-4 rounded-3xl border border-white/10 bg-[#0b1727] p-4">
                  <div className="text-xs uppercase tracking-[0.22em] text-slate-500">Customer details</div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <input value={editor.name} onChange={(e) => setEditor((prev) => ({ ...prev, name: e.target.value }))} placeholder="Full name" style={inputStyle} />
                    <input value={editor.phone} onChange={(e) => setEditor((prev) => ({ ...prev, phone: e.target.value }))} placeholder="Phone / WhatsApp" style={inputStyle} />
                    <input value={editor.suburb} onChange={(e) => setEditor((prev) => ({ ...prev, suburb: e.target.value }))} placeholder="Suburb" style={inputStyle} />
                    <input value={editor.addressLine1} onChange={(e) => setEditor((prev) => ({ ...prev, addressLine1: e.target.value }))} placeholder="Full delivery address" style={inputStyle} />
                  </div>
                  <textarea value={editor.notes} onChange={(e) => setEditor((prev) => ({ ...prev, notes: e.target.value }))} placeholder="Access notes, delivery notes, common requests" rows={6} style={{ ...inputStyle, resize: 'vertical' }} />
                </div>

                <div className="space-y-4 rounded-3xl border border-white/10 bg-[#0b1727] p-4">
                  <div>
                    <div className="text-xs uppercase tracking-[0.22em] text-slate-500">Pilot actions</div>
                    <div className="mt-2 text-sm text-slate-400">
                      This page is the CRM light layer. Next phase can add delivery history, repeat last order and save-without-order.
                    </div>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-slate-300">
                    <div className="flex items-center justify-between"><span>Created from intake</span><strong className="text-white">Yes</strong></div>
                    <div className="mt-2 flex items-center justify-between"><span>Editable now</span><strong className="text-white">{canEdit ? 'Yes' : 'View only'}</strong></div>
                    <div className="mt-2 flex items-center justify-between"><span>Repeat order</span><strong className="text-slate-400">Next phase</strong></div>
                  </div>
                  <button
                    type="submit"
                    disabled={saving || !canEdit}
                    className="w-full rounded-2xl border border-cyan-400/20 bg-cyan-500/15 px-4 py-3 text-sm font-medium text-cyan-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : canEdit ? 'Save customer updates' : 'View only'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
